import {
  ArchiveOutlined,
  DeleteOutline,
  EditOutlined,
  HistoryOutlined,
  LockOutlined,
  UnarchiveOutlined,
} from '@mui/icons-material'
import { Box, Button, Chip, Divider, IconButton, Paper, Stack, Tooltip, Typography } from '@mui/material'
import dayjs from 'dayjs'
import { FC } from 'react'
import { DocPageDetailFragment, DocPageScope, DocPageStatus } from '../../../generated'
import { DOCS_LABELS } from './docs.constants'
import { RichTextRenderer } from './editor/RichTextRenderer'

interface DocPageViewProps {
  page: DocPageDetailFragment
  canManage: boolean
  onEdit: () => void
  onArchive: () => void
  onUnarchive: () => void
  onDelete: () => void
  onHistory: () => void
}

const fullName = (user?: { firstName?: string | null; lastName?: string | null } | null) =>
  user ? `${user.firstName || ''} ${user.lastName || ''}`.trim() : ''

export const DocPageView: FC<DocPageViewProps> = ({
  page,
  canManage,
  onEdit,
  onArchive,
  onUnarchive,
  onDelete,
  onHistory,
}) => {
  // Readers see published content; admins fall back to the draft for unpublished pages.
  const content = page.content || (canManage ? page.draftContent : null)
  const updatedBy = fullName(page.updatedBy)
  const isArchived = page.status === DocPageStatus.Archived

  return (
    <Box sx={{ maxWidth: 1040, mx: 'auto', width: '100%' }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}>
        <Box sx={{ minWidth: 0 }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap' }}>
            <Typography variant="h4" sx={{ fontWeight: 700 }}>
              {page.icon ? `${page.icon} ` : ''}
              {page.title || DOCS_LABELS.UNTITLED}
            </Typography>
            {page.scope === DocPageScope.Personal && (
              <Chip
                size="small"
                icon={<LockOutlined sx={{ fontSize: 14 }} />}
                label={DOCS_LABELS.VISIBILITY_PRIVATE}
                variant="outlined"
                sx={{ height: 22 }}
              />
            )}
            {page.status !== DocPageStatus.Published && (
              <Chip
                size="small"
                label={page.status === DocPageStatus.Draft ? DOCS_LABELS.DRAFT_BADGE : DOCS_LABELS.ARCHIVED_BADGE}
                color={page.status === DocPageStatus.Draft ? 'warning' : 'default'}
                sx={{ height: 22 }}
              />
            )}
          </Stack>
          {page.updatedAt && (
            <Typography variant="caption" color="text.secondary">
              Last updated {dayjs(page.updatedAt).format('MMM D, YYYY h:mm A')}
              {updatedBy ? ` by ${updatedBy}` : ''}
            </Typography>
          )}
        </Box>

        {canManage && (
          <Stack direction="row" spacing={1} alignItems="center" sx={{ flexShrink: 0 }}>
            <Button
              variant="contained"
              startIcon={<EditOutlined />}
              onClick={onEdit}
              sx={{ borderRadius: 2, px: 2.25, boxShadow: 'none', textTransform: 'none', fontWeight: 600 }}
            >
              {DOCS_LABELS.EDIT}
            </Button>
            {/* Secondary actions grouped into a single subtle, segmented control. */}
            <Paper
              variant="outlined"
              sx={{
                display: 'flex',
                alignItems: 'center',
                borderRadius: 2,
                overflow: 'hidden',
                '& .MuiIconButton-root': { borderRadius: 0, px: 1 },
                '& .MuiIconButton-root:not(:last-of-type)': {
                  borderRight: '1px solid',
                  borderColor: 'divider',
                },
              }}
            >
              <Tooltip title={DOCS_LABELS.HISTORY}>
                <IconButton size="small" onClick={onHistory}>
                  <HistoryOutlined fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title={isArchived ? DOCS_LABELS.UNARCHIVE : DOCS_LABELS.ARCHIVE}>
                <IconButton size="small" onClick={isArchived ? onUnarchive : onArchive}>
                  {isArchived ? <UnarchiveOutlined fontSize="small" /> : <ArchiveOutlined fontSize="small" />}
                </IconButton>
              </Tooltip>
              <Tooltip title={DOCS_LABELS.DELETE}>
                <IconButton size="small" color="error" onClick={onDelete}>
                  <DeleteOutline fontSize="small" />
                </IconButton>
              </Tooltip>
            </Paper>
          </Stack>
        )}
      </Box>
      <Divider sx={{ my: 2 }} />
      <RichTextRenderer content={content} />
    </Box>
  )
}
